import * as CardCrawl from "@ravalmatic/cardcrawl-engine";
import { GameManager } from "../../GameManager";
import { AudioManager } from "../../AudioManager";
import { GameVars } from "../../GameVars";
import { HUD } from "../board-scene/hud/HUD";
import { GameConstants } from "../../GameConstants";
import { BackgroundContainer } from "../board-scene/BackgroundContainer";
import { BoardContainer } from "../board-scene/BoardContainer";
import { MessagesLayer } from "../board-scene/MessagesLayer";
import { GameOverLayer } from "../board-scene/game-over-layer/GameOverLayer";
import { GUI } from "../board-scene/gui/GUI";
import { TutorialManager } from "./TutorialManager";
import { PlayerCard } from "../board-scene/cards/PlayerCard";
import { HandCursor } from "./HandCursor";
import { ButtonWithText } from "../../utils/ButtonWithText";
import  {EventType, MessagePosition} from "../../Types";

export class TutorialScene extends Phaser.Scene {

    public static currentInstance: TutorialScene;

    public hud: HUD;
    public gameOverLayer: GameOverLayer;

    private gui: GUI;
    private boardContainer: BoardContainer;
    private backgroundContainer: BackgroundContainer;
    private messagesLayer: MessagesLayer;
    private tmpPlayerCardContainer: Phaser.GameObjects.Container;
    private tutorialStepLabel: Phaser.GameObjects.Text;
    private tutorialRequiredActionLabel: Phaser.GameObjects.Text;
    private handCursor: HandCursor; 

    constructor() {

        super("TutorialScene");
        
        TutorialScene.currentInstance = this;
    }

    public create(): void {

        GameManager.setCurrentScene(this);

        const lan = GameVars.gameData.language;
        const text = GameVars.currentScene.cache.json.get("text");

        this.messagesLayer = null;
        this.tmpPlayerCardContainer = null;

        const tmpBackground = this.add.graphics();
        tmpBackground.fillStyle(0x000000);
        tmpBackground.fillRect(0, 0, GameConstants.GAME_WIDTH, GameConstants.GAME_HEIGHT);

        if (!this.anims.exists("foil_fx")) {
            this.anims.create(
                {key: "foil_fx", 
                frames: this.anims.generateFrameNames("texture_atlas_2", {prefix: "foil_fx_", start: 1, end: 20, zeroPad: 2, suffix: ""}), 
                frameRate: 20, 
                repeat: -1
            });
        }

        if (!this.anims.exists("slash_fx")) {
            this.anims.create(
                {key: "slash_fx", 
                frames: this.anims.generateFrameNames("texture_atlas_1", {prefix: "slash_fx_", start: 1, end: 21, zeroPad: 2, suffix: ""}), 
                frameRate: 32,
                hideOnComplete: true
            });
        }

        if (!this.anims.exists("chest_hover_fx")) {
            this.anims.create(
                {key: "chest_hover_fx", 
                frames: this.anims.generateFrameNames("texture_atlas_2", {prefix: "fx_hover_chest_", start: 1, end: 24, zeroPad: 2, suffix: ""}), 
                frameRate: 24,
                repeat: -1
            });
        }

        if (!this.anims.exists("flame_fx")) {
            this.anims.create(
                {key: "flame_fx", 
                frames: this.anims.generateFrameNames("texture_atlas_3", {prefix: "spell_", start: 0, end: 21, zeroPad: 2, suffix: ""}), 
                frameRate: 24,
                hideOnComplete: true
            });
        }

        if (!this.anims.exists("press_card_fx")) {
            this.anims.create(
                {key: "press_card_fx", 
                frames: this.anims.generateFrameNames("texture_atlas_1", {prefix: "press_hold_", start: 1, end: 18, zeroPad: 2, suffix: ""}), 
                frameRate: 24,
                hideOnComplete: true
            });
        }

        this.backgroundContainer = new BackgroundContainer(this);
        this.add.existing(this.backgroundContainer);

        this.boardContainer = new BoardContainer(this);
        this.boardContainer.deck.visible = false;
        this.boardContainer.upperTableauContainer.visible = false;
        this.boardContainer.lowerTableauContainer.visible = false;
        this.boardContainer.chest.visible = false;
        this.add.existing(this.boardContainer);

        this.hud = new HUD(this);
        this.add.existing(this.hud);

        this.gui = new GUI(this);
        this.gui.menuButton.visible = false;
        this.gui.mulliganButton.visible = false;
        this.add.existing(this.gui);

        TutorialManager.init();

        this.input.on("pointerdown", this.onPointerDown, this);

        if (GameConstants.DEVELOPMENT) {
            this.tutorialStepLabel = this.add.text(10,  GameConstants.GAME_HEIGHT - 50 * GameVars.scaleY, "Tutorial step: 1", {fontFamily: "Verdana", fontSize: "18px", color: "#FFFFFF"});
            this.tutorialStepLabel.scaleY = GameVars.scaleY;
            this.tutorialRequiredActionLabel = this.add.text(10, GameConstants.GAME_HEIGHT - 25 * GameVars.scaleY, "Required action: CLICK", {fontFamily: "Verdana", fontSize: "18px", color: "#FFFFFF"});
            this.tutorialRequiredActionLabel.scaleY = GameVars.scaleY;
        }

        GameVars.eventsDispatcher.addEventListener(EventType.REMOVE_TUTORIAL_CURSOR, this.onRemoveHandCursor, this);

        const skipButtonLabel = new Phaser.GameObjects.Text(this, 0, -4, text.SKIP[lan], {fontFamily: "AlmendraBold", color: "#663227", fontSize: "46px"});
        skipButtonLabel.setOrigin(.5);

        if (skipButtonLabel.width > 125) {
            skipButtonLabel.scale = 125 / skipButtonLabel.width;
        }

        const skipButtonImage = new Phaser.GameObjects.Image(this, 0, 0, "texture_atlas_1", "btn_next_game_over");
        skipButtonImage.setScale(.85);

        const skipButton = new ButtonWithText(this, skipButtonImage, skipButtonLabel, this.onClickSkip, this);
        skipButton.x = 90;
        skipButton.y = 50 * GameVars.scaleY;
        skipButton.scaleY = GameVars.scaleY;
        this.add.existing(skipButton);
    
        AudioManager.stopSound("splash_loop");
        AudioManager.stopSound("victory_loop");
        AudioManager.stopSound("gameover_loop");
        AudioManager.stopSound("soundtrack_loop");

        AudioManager.playSound("soundtrack_loop", true, 2);
    }

    public update(): void {

        TutorialManager.update();

        this.gui.update();
        this.hud.update();

        if (GameConstants.DEVELOPMENT) {
            this.tutorialStepLabel.text = "Tutorial step: " + GameVars.tutorialStepData.step;
            this.tutorialRequiredActionLabel.text = "Required action: " + GameVars.tutorialStepData.action;
        }
    }

    public processStep(): void {

        switch (GameVars.tutorialStepData.step) {

            case 1:

                this.time.delayedCall(500, () => {
                    this.boardContainer.deck.visible = true;
                    this.boardContainer.startTutorial();
                }, [], this);

                break;

            case 2:

                this.tmpPlayerCardContainer = this.add.container(GameConstants.GAME_WIDTH / 2, GameConstants.GAME_HEIGHT / 2 + (BoardContainer.LOWER_TABLEAU_CONTAINER_Y + BoardContainer.CARD_HEIGHT / 2) * GameVars.scaleY);
                this.tmpPlayerCardContainer.scaleY = GameVars.scaleY;
        
                const frame = this.add.image(0, 0, "texture_atlas_1", "position_highlight_cut");
                this.tmpPlayerCardContainer.add(frame);
        
                frame.setScale(1.1);
                this.tweens.add({
                    targets: frame,
                    scaleX: 1,
                    scaleY: 1,
                    duration: 1000,
                    ease: Phaser.Math.Easing.Cubic.Out,
                    repeat: -1,
                    yoyo: false,
                    loopDelay: 300
                });
        
                const tmpPlayerCard = new PlayerCard(this, {x: 0, y: 0, angle: 0}, CardCrawl.Utils.getCardData(0, CardCrawl.CardType.PLAYER, 13));
                this.tmpPlayerCardContainer.add(tmpPlayerCard);

                break;

            case 3:

                this.tmpPlayerCardContainer.destroy();
                this.tmpPlayerCardContainer = null;
                
                this.boardContainer.upperTableauContainer.visible = true;

                this.boardContainer.lowerTableauContainer.visible = true;
                this.boardContainer.lowerTableauContainer.slots[0].visible = false;
                this.boardContainer.lowerTableauContainer.slots[1].visible = false;
                this.boardContainer.lowerTableauContainer.slots[2].visible = false;

                this.backgroundContainer.startGame();
                this.gui.startGame();
                this.hud.startGame();

                this.time.delayedCall(GameVars.tutorialStepData.delay + 750, () => {

                    if (GameVars.tutorialStepData.step === 3) {
                        this.boardContainer.upperTableauContainer.showAllowedMoves(null);
                    }
                }, [], this);
                
                break;

            case 4:

                this.boardContainer.upperTableauContainer.hideAllowedMoves();

                this.time.delayedCall(GameVars.tutorialStepData.delay + 1500, () => {

                    this.onRemoveHandCursor();
                    
                    this.handCursor = new HandCursor(this);
                    this.add.existing(this.handCursor);
                }, [], this);

                this.time.delayedCall(GameVars.tutorialStepData.delay + 1250, () => {
                    this.boardContainer.lowerTableauContainer.slots[0].visible = true;       
                }, [], this);

                break;

            case 5:

                this.time.delayedCall(GameVars.tutorialStepData.delay + 1000, () => {

                    this.onRemoveHandCursor();

                    this.handCursor = new HandCursor(this);
                    this.add.existing(this.handCursor);
                }, [], this);

                this.time.delayedCall(GameVars.tutorialStepData.delay + 1000, () => {
                    this.boardContainer.lowerTableauContainer.slots[1].visible = true;
                }, [], this);

                break;

            case 6:
                
                this.time.delayedCall(GameVars.tutorialStepData.delay + 1000, () => {

                    this.onRemoveHandCursor();

                    this.handCursor = new HandCursor(this);
                    this.add.existing(this.handCursor);  
                    
                    this.boardContainer.lowerTableauContainer.slots[2].visible = true;
                }, [], this);
   
                break;

            case 7:

                // console.log("HIGHLIGHT 3 CANDADOS");

                break;

            case 8:

                this.time.delayedCall(GameVars.tutorialStepData.delay + 1000, () => {

                    this.onRemoveHandCursor();

                    this.handCursor = new HandCursor(this);
                    this.add.existing(this.handCursor);
                }, [], this);

                break;

            case 9:

                this.onRemoveHandCursor();

                this.handCursor = new HandCursor(this);
                this.add.existing(this.handCursor);

                break;

            case 10:

                this.onRemoveHandCursor();

                this.handCursor = new HandCursor(this);
                this.add.existing(this.handCursor);

                break;

            case 11:

                this.onRemoveHandCursor();

                this.handCursor = new HandCursor(this);
                this.add.existing(this.handCursor);

                this.time.delayedCall(GameVars.tutorialStepData.delay + 1000, () => {
                    this.boardContainer.chest.startGame();
                    this.boardContainer.chest.visible = true;
                }, [], this);

                break;

            case 12:
                
                break;

            case 13:

                this.gui.mulliganButton.visible = true;
                this.time.delayedCall(GameVars.tutorialStepData.delay + 750, () => {
                    this.gui.mulliganButton.showHandCursor();
                }, [], this);

                break;

            case 14:

                BoardContainer.currentInstance.chest.onCardOver(null);

                break;

            case 15:

                this.onRemoveHandCursor();

                this.handCursor = new HandCursor(this);
                this.add.existing(this.handCursor);

                break;

            case 16:

                this.onRemoveHandCursor();

                this.handCursor = new HandCursor(this);
                this.add.existing(this.handCursor);

                break;

            case 17:

                this.time.delayedCall(GameVars.tutorialStepData.delay + 1000, () => {

                    this.onRemoveHandCursor();

                    this.handCursor = new HandCursor(this);
                    this.add.existing(this.handCursor);
                }, [], this);
                break;

            case 18:

                console.log("tuturial step 18");

                this.time.delayedCall(GameVars.tutorialStepData.delay + 2000, () => {

                    this.onRemoveHandCursor();

                    this.handCursor = new HandCursor(this);
                    this.add.existing(this.handCursor);
                }, [], this);
                break;

            case 19:

                this.onRemoveHandCursor();

                this.handCursor = new HandCursor(this);
                this.add.existing(this.handCursor);

                break;

            case 20:

                this.time.delayedCall(GameVars.tutorialStepData.delay + 500, () => {

                    this.onRemoveHandCursor();
                    
                    this.handCursor = new HandCursor(this);
                    this.add.existing(this.handCursor);
                }, [], this);
                break;

            default:
        }

        this.hideMessageLayer();

        if (GameVars.tutorialStepData.message) {
            this.showMessageLayer(GameVars.tutorialStepData.message, GameVars.tutorialStepData.messagePosition, GameVars.tutorialStepData.delay, GameVars.tutorialStepData.action);
        }
    }

    public onActionProcessed(): void {

        this.backgroundContainer.onActionProcessed();

        this.boardContainer.onActionProcessed();
        this.hud.onActionProcessed();
    }

    public onGameOver(): void {

        this.backgroundContainer.onGameOver();
        this.boardContainer.onGameOver();
        this.hud.onGameOver();
        this.gui.onGameOver();

        this.gameOverLayer = new GameOverLayer(this);
        this.add.existing(this.gameOverLayer);

        AudioManager.stopSound("soundtrack_loop");

        if (GameVars.victory) {
            AudioManager.playSound("successful_run");
            AudioManager.playSound("victory_loop", true);
        } else {
            AudioManager.playSound("gameover_loop", true);
        }
    }

    public showMessageLayer(msg: string, position: MessagePosition, delay: number, requiredAction: EventType): void {

        if (this.messagesLayer) {
            this.messagesLayer.destroy();
        }

        this.messagesLayer = new MessagesLayer(this, msg, position, delay, requiredAction);
        this.add.existing(this.messagesLayer);
    }

    public hideMessageLayer(): void {

        if (this.messagesLayer === null) {
            return;
        }
        
        this.messagesLayer.destroy();
        this.messagesLayer = null;
    }

    public onCardDealt(): void {

        this.hud.onCardDealt();
    }
    
    private onPointerDown(): void {

        const now = Date.now();

        if (GameVars.tutorialStepData.action === EventType.CLICK && now > GameVars.tutorialStepData.t + GameVars.tutorialStepData.delay) {
            TutorialManager.processStep();
        }
    }

    private onRemoveHandCursor(): void {

        if (this.handCursor) {
            this.handCursor.destroy();
            this.handCursor = null;
        }
    }

    private onClickSkip(): void {

        GameManager.enterSplashScene();
    }
}
